import * as React from 'react';
import {faFilePdf} from '@fortawesome/pro-regular-svg-icons';
import {faPrint, faShareSquare} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {helmetJsonLdProp} from 'react-schemaorg';
import {Helmet, HelmetProvider} from 'react-helmet-async';
import {useEffect, useMemo, useState} from 'react';

import {ActionBlock} from './ActionBlock';
import {Alerts} from '../../ui/Alerts/Alerts';
import {AlternateProductsModal} from '../AlternateProductsModal/AlternateProductsModal';
import {AlternateProducts} from './AlternateProducts';
import {AnonymousPriceVisibility, gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {AssortmentContains} from './AssortmentContains';
import {AssortmentItem, Item, OrderableItem, ShareItemRes, WebAsset} from '../item.class';
import {AvailableAssortments} from './AvailableAssortments';
import {Badges} from './Badges';
import {BreadcrumbList, ListItem, Offer, Product, WithContext} from 'schema-dts';
import {CompareItems} from '../compare/CompareItems';
import {CompareModals} from '../compare/CompareFunctions';
import {ExpertlyCrafted} from './ExpertlyCrafted/ExpertlyCrafted';
import {FrequentlyBoughtTogether} from '../FrequentlyBoughtTogether/FrequentlyBoughtTogether';
import {gaViewItem} from '../../../client/ga/ga-ecommerce.functions';
import {IDPExceptions} from './IDPExceptions';
import {IDPTabs} from './IDPTabs';
import {ImpError} from '../../../client/imp-error/imp-error.class';
import {ItemSpinner} from '../item-spinner/ItemSpinner';
import {ItemsService} from '../../../client/items/items.service';
import {MonetateService} from '../../../client/monetate/monetate.service';
import {OrdersService} from '../../../client/orders/orders.service';
import {PrimaryImage} from './PrimaryImage';
import {RecosScheme} from '../../monetate/monetate.types';
import {RequiredItems} from './RequiredItems';
import {ShareLinkModal} from '../../share-link/ShareLinkModal';
import {ShareLinkParams} from '../../share-link/share-link.class';
import {showBarcodePopUp} from '../../../client/tools/show-barcode-pop-up';
import {SupportingDocuments} from './SupportingDocuments';
import {SwitchAndSave} from '../SwitchAndSave/SwitchAndSave';
import {TechSpecs} from './TechSpecs';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import Button from '../../ui/Buttons/Button';
import {PageHeader} from '../../ui/PageHeader/PageHeader';

declare let digitalData: {product: unknown}; // For Reco datalayer

export const SHIPPING_SAVER = `8000000`;

export interface ItemDetailPageProps {
    alternateProducts?: OrderableItem[];
    assortmentItems: AssortmentItem[];
    brandLink: string;
    categoryPath: string;
    disableRecaptcha: boolean;
    globalErrorMessage: string;
    hasDocuments: boolean;
    helmetContext: any;
    isInternalUser: boolean;
    itemData: Item;
    showExceptions: boolean;
    userData: User;
}

export const ItemDetailPage = ({
    alternateProducts,
    assortmentItems,
    brandLink,
    categoryPath,
    disableRecaptcha,
    globalErrorMessage,
    hasDocuments,
    helmetContext,
    isInternalUser,
    itemData,
    showExceptions,
    userData,
}: ItemDetailPageProps) => {
    const user = useMemo(() => new User(userData), [userData]);
    const item = useMemo(() => new Item(itemData), [itemData]);

    const [isClient, setIsClient] = useState(false);
    const [product1_rr, setProduct1_rr] = useState<RecosScheme>();
    const [product2_rr, setProduct2_rr] = useState<RecosScheme>();
    const [shareLinkErr, setShareLinkErr] = useState(``);
    const [shareLinkModalTitle, setShareLinkModalTitle] = useState(undefined);
    const [shareLinkRes, setShareLinkRes] = useState<ShareItemRes>(null);
    const [shareLinkShowForm, setShareLinkShowForm] = useState(true);
    const [shareLinkShowSuccess, setShareLinkShowSuccess] = useState(false);
    const [shareLinkShowWarning, setShareLinkShowWarning] = useState(false);
    const [showShareLinkModalState, setShowShareLinkModalState] = useState(false);
    const componentName = `ItemDetailPage`;
    const itemsService: ItemsService = useService(`itemsService`);
    const monetateService: MonetateService = useService(`monetateService`);
    const ordersService: OrdersService = useService(`ordersService`);

    categoryPath = categoryPath || ``;
    /**
     * submitShare IDP
     * @param shareLinkParams
     */
    const shareList = (shareLinkParams: ShareLinkParams) => {
        setShareLinkErr(``);
        itemsService
            .shareItem(shareLinkParams)
            .then((shareListRes) => {
                setShareLinkRes(shareListRes);
                if (shareListRes.result === `OK`) {
                    setShareLinkModalTitle(`Thank you`);
                    setShareLinkShowSuccess(true);
                    setShareLinkShowForm(false);
                } else if (shareListRes.result === `WARNING`) {
                    setShareLinkModalTitle(`Error`);
                    setShareLinkModalTitle(`Error`);
                    setShareLinkShowWarning(true);
                    setShareLinkShowWarning(true);
                    setShareLinkShowSuccess(false);
                    setShareLinkShowForm(false);
                }
            })
            .catch((shareListErr: ImpError) => {
                setShareLinkErr(shareListErr.message);
            });
    };
    /**
     * Resets ShareLinkModal state on close
     */
    const handleCloseShareLinkModal = () => {
        // Close modal
        setShowShareLinkModalState(false);

        // Reset modal state to default values
        setShareLinkErr(``);
        setShareLinkModalTitle(undefined);
        setShareLinkRes(null);
        setShareLinkShowForm(true);
        setShareLinkShowSuccess(false);
        setShareLinkShowWarning(false);
    };

    useEffect(() => {
        setIsClient(true);
    }, []);

    const [errorMessage, setErrorMessage] = useState(``);
    const [showAlternates, setShowAlternates] = useState(false);

    const shopOthersButton = useMemo(() => {
        if (
            (item.price && item.price.error === `BLOCKED`) ||
            (!item.inStock && !item.isdiscontinue && item.itemtype?.toUpperCase() !== `D`) ||
            !item.canPurchase ||
            (item.pandemicItem && item.pandemicItemBlocked)
        ) {
            // Test if the item has applicable alternate products
            if (alternateProducts?.length > 0) {
                return `shopAlternateProducts`;
            } else {
                return `shopCategory`;
            }
        }
        return ``;
    }, [item, alternateProducts]);
    let caSupportingDocs: WebAsset[];
    if (item?.supportingDocuments?.length > 0) {
        caSupportingDocs = item.supportingDocuments.filter((supportingDocument) => {
            return supportingDocument.fileLocation && supportingDocument.referenceType === `california_support_document`;
        });
    }
    const productSchema: WithContext<Product> = {
        '@context': `https://schema.org`,
        '@type': `Product`,
        gtin8: `https://www.imperialsupplies.com/barcode-generator?code=${item.id}`,
        description: item.name,
        name: item.h1SeoOverride || item.h1HTML || ``,
        brand: {
            '@type': `Brand`,
            name: item.brand_name_s ? item.brand_name_s : ``,
        },
        sku: item.id,
        image: `https://www.imperialsupplies.com/ProductImageThumbs400${item.imagePath || '/noimage.png'}`,
        offers: {
            '@type': `Offer`,
            availability: !item.pandemicItemBlocked && item.inStock ? `https://schema.org/InStock` : `https://schema.org/OutOfStock`,
            url: `https://www.imperialsupplies.com/item/${item.id}`,
        },
    };
    if (item.price) {
        (productSchema.offers as Offer).price = item.price.pricePkg || ``;
        (productSchema.offers as Offer).priceCurrency = `USD`;
    } else if (item.webPrice) {
        (productSchema.offers as Offer).price = item.webPrice.pricePkg || ``;
        (productSchema.offers as Offer).priceCurrency = `USD`;
    }
    let breadCrumbsSchema: WithContext<BreadcrumbList>;
    if (item.breadcrumbs) {
        const itemListElement: ListItem[] = [
            {'@type': `ListItem`, position: 1, name: `Home`, item: `https://www.imperialsupplies.com/`},
            {'@type': `ListItem`, position: 2, name: `All Products`, item: `https://www.imperialsupplies.com/browse/`},
        ];
        breadCrumbsSchema = {
            '@context': `https://schema.org`,
            '@type': `BreadcrumbList`,
            itemListElement,
        };
        item.breadcrumbs.forEach((bc, i) => {
            itemListElement.push({
                '@type': `ListItem`,
                position: i + 3,
                name: bc.name,
                item: `https://www.imperialsupplies.com/browse/${bc.categoryPath}`,
            });
        });
        itemListElement.push({
            '@type': `ListItem`,
            position: item.breadcrumbs.length + 2,
            name: `SKU: ${item.prettyid}`,
            item: `https://www.imperialsupplies.com/item/${item.id}`,
        });
    }

    const getPandemicAndStockWarnings = () => {
        if (item.pandemicItem && item.pandemicItemBlocked) {
            if (shopOthersButton === `shopCategory`) {
                return (
                    <Alerts
                        alertLinkHidden={false}
                        alertLinkHref={`/browse/${categoryPath}`}
                        alertLinkText="Shop Category"
                        message="This item is currently unavailable."
                        variant="warning"
                    />
                );
            } else {
                return (
                    <Alerts
                        alertLinkHidden={false}
                        alertLinkText="Shop Alternates"
                        message="This item is currently unavailable."
                        onClickAlertLink={() => {
                            setShowAlternates(true);
                        }}
                        variant="warning"
                    />
                );
            }
        } else if (item.ondemandDays_i && !item.inStock) {
            item.invStatus = `Estimated business days to ship: ${item.ondemandDays_i}`;
            item.statColor = ``;
            return (
                <Alerts
                    alertLinkHidden={false}
                    alertLinkText="Shop Alternates"
                    message={`Estimated business days to ship: ${item.ondemandDays_i}`}
                    onClickAlertLink={() => {
                        setShowAlternates(true);
                    }}
                    variant="warning"
                />
            );
        } else if (!item.inStock && !item.isdiscontinue && item.itemtype?.toUpperCase() !== `D`) {
            if (shopOthersButton === `shopCategory`) {
                return (
                    <Alerts
                        alertLinkHidden={false}
                        alertLinkHref={`/browse/${categoryPath}`}
                        alertLinkText="Shop Category"
                        message="Out of stock. Call your Dedicated Account Advisor for availability."
                        variant="warning"
                    />
                );
            } else {
                return (
                    <Alerts
                        alertLinkHidden={false}
                        alertLinkText="Shop Alternates"
                        message="Out of stock. Call your Dedicated Account Advisor for availability."
                        onClickAlertLink={() => {
                            setShowAlternates(true);
                        }}
                        variant="warning"
                    />
                );
            }
        }
    };

    const getDropShipWarnings = () => {
        if (shopOthersButton) {
            return <></>;
        }
        // Determine drop-ship messaging
        if (
            (item.itemtype?.toUpperCase() === `D` || item.itemtype?.toUpperCase() === `L`) &&
            item.invngroup !== `GRA` &&
            item.id !== SHIPPING_SAVER // (ICOM-2967)
        ) {
            const dropShipWarning = [];
            if (item.dropshipDays_i > 1) {
                dropShipWarning.push(`Estimated business days to ship: ${item.dropshipDays_i}`);
            }
            dropShipWarning.push(`Additional freight charges may apply for this item.`);
            if (dropShipWarning.length) {
                return (
                    <Alerts
                        alertLinkHidden={false}
                        alertLinkText="Shop Alternates"
                        message={dropShipWarning.join(` | `)}
                        onClickAlertLink={() => {
                            setShowAlternates(true);
                        }}
                        variant="warning"
                    />
                );
            }
        }
    };

    // Load Spinners
    useEffect(() => {
        monetateService
            .getRecoItems({
                itemid: item.id,
                scheme: `product1_rr;product2_rr`,
            })
            .then((getRecoItemsRes) => {
                getRecoItemsRes.forEach((scheme) => {
                    if (scheme.scheme === `product1_rr`) {
                        setProduct1_rr(scheme);
                    }
                    if (scheme.scheme === `product2_rr`) {
                        setProduct2_rr(scheme);
                    }
                });
            })
            .catch(() => {
                // error silently
            });
        // Only On Initial page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Builds and sends itemDetail analytics
    useEffect(() => {
        // Adds item data to the dataLayer to report product view to Reco
        digitalData.product = {
            CategoryId: item.webcatid,
            ProductId: item.id,
            ProductNm: item.name,
        };

        // Determine AnonymousPriceVisibility
        let anonymousPriceVisibility: AnonymousPriceVisibility;
        if (!user.isLoggedIn()) {
            anonymousPriceVisibility = item.webPrice ? `Yes` : `No`;
        }

        // Send itemDetail analytics
        gaViewItem({
            dimension16: ordersService.currentOrderNumber,
            dimension29: anonymousPriceVisibility,
            dimension40: item.inStock ? `InStock` : `NoStock`,
            item_id: item.id,
        });
        monetateService.itemDetail(item.id);

        // Only On Initial page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Template
     */
    if (globalErrorMessage) {
        return (
            <Alerts
                message={globalErrorMessage}
                variant="danger"
            />
        );
    }
    return (
        <HelmetProvider context={helmetContext}>
            <div data-e2e="item-detail-component">
                <Alerts
                    message={errorMessage}
                    variant="danger"
                />
                {item.pricingStatus === `Blocked` && (
                    <Alerts
                        message="Item has been blocked from ordering per your request"
                        variant="danger"
                    />
                )}
                {item.pricingStatus === `Convert` && (
                    <Alerts
                        alertLinkHidden={!!(item.price && item.price.error)}
                        alertLinkHref={`/item/${item.price && item.price.error ? item.price.error : ''}`}
                        alertLinkText={item.price && item.price.error ? item.price.error : ``}
                        message={`Item has been converted to ${Item.prettyItem(
                            item.price && item.price.error ? item.price.error : '',
                        )} per your request`}
                        variant="danger"
                    />
                )}
                {item.invMsg && (
                    <Alerts
                        message={item.invMsg}
                        variant="warning"
                    />
                )}
                {item.alertMessage && (
                    <Alerts
                        message={item.alertMessage}
                        variant="warning"
                    />
                )}
                {item.isdiscontinue && (
                    <>
                        {item.inStock && item.itemtype !== `D` && (
                            <Alerts
                                message="Item has been discontinued. Purchase while supplies last!"
                                variant="warning"
                            />
                        )}
                        {shopOthersButton === `shopCategory` && (
                            <Alerts
                                alertLinkHidden={false}
                                alertLinkHref={`/browse/${categoryPath}`}
                                alertLinkText="Shop Category"
                                message="This product has been discontinued and is no longer available."
                                variant="warning"
                            />
                        )}
                        {shopOthersButton === `shopAlternateProducts` && (
                            <Alerts
                                alertLinkHidden={false}
                                alertLinkText="Shop Alternates"
                                message="This product has been discontinued and is no longer available."
                                onClickAlertLink={() => {
                                    setShowAlternates(true);
                                }}
                                variant="warning"
                            />
                        )}
                    </>
                )}
                {getPandemicAndStockWarnings()}
                {getDropShipWarnings()}
                <div className="tw-mb-3 tw-text-right print:tw-hidden">
                    <div
                        className="tw-inline-block tw-cursor-pointer"
                        onClick={() => {
                            setShowShareLinkModalState(true);
                            gaLegacyCustomEvent({
                                eventAction: `IDP Email Button Click`,
                                eventCategory: `Email Item`,
                                eventLabel: `/item/${item.id}`,
                            });
                        }}
                    >
                        Share&nbsp;&nbsp;
                        <FontAwesomeIcon
                            icon={faShareSquare}
                            size="2x"
                            suppressHydrationWarning
                        />
                    </div>
                    <ShareLinkModal
                        disableRecaptcha={disableRecaptcha}
                        linkId={item.id}
                        onClose={handleCloseShareLinkModal}
                        onShareLink={shareList}
                        shareLinkErr={shareLinkErr}
                        shareLinkModalTitle={shareLinkModalTitle}
                        shareLinkRes={shareLinkRes}
                        shareLinkShowForm={shareLinkShowForm}
                        shareLinkShowSuccess={shareLinkShowSuccess}
                        shareLinkShowWarning={shareLinkShowWarning}
                        shareLinkType="item"
                        show={showShareLinkModalState}
                        user={user}
                    />
                    <div
                        className="tw-inline-block tw-cursor-pointer tw-ml-3"
                        onClick={() => {
                            window.print();
                        }}
                    >
                        Print&nbsp;&nbsp;
                        <FontAwesomeIcon
                            icon={faPrint}
                            size="2x"
                            suppressHydrationWarning
                        />
                    </div>
                </div>
                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12">
                    <div
                        className="tw-col-start-1 tw-col-span-1 lg:tw-col-start-6 lg:tw-col-span-7 tw-relative tw-w-full tw-pl-0"
                        data-e2e="productDetails"
                    >
                        <div className="tw-mb-3 print:tw-hidden">
                            <Badges item={item} />
                        </div>
                        {item.brand_name_s && brandLink ? <a href={brandLink}>{item.brand_name_s}</a> : <span>{item.brand_name_s}</span>}
                        <h1
                            className="tw-font-normal tw-border-b tw-leading-[1.2] tw-text-[23px] lg:tw-text-2xl tw-flex tw-flex-nowrap tw-justify-between tw-items-center tw-mt-0 tw-mb-6"
                            dangerouslySetInnerHTML={{__html: item.h1SeoOverride || item.h1HTML}}
                        ></h1>
                    </div>
                </div>
                <div className="tw-flex tw-flex-wrap">
                    <div className="tw-w-full md:tw-w-1/2 lg:tw-w-1/3 lg:-tw-mt-6">
                        <PrimaryImage item={item} />
                    </div>
                    <div className="tw-w-1/12" />
                    <div className="tw-w-full md:tw-w-5/12 lg:tw-w-7/12">
                        <ActionBlock
                            categoryPath={categoryPath}
                            item={item}
                            onError={setErrorMessage}
                            setShowAlternates={setShowAlternates}
                            shopOthersButton={shopOthersButton}
                            user={user}
                        />
                    </div>
                </div>
                {item.switchAndSaveItem && (
                    <SwitchAndSave
                        percentSaved={item.switchAndSaveItem.pctDiff}
                        replacementItem={item.switchAndSaveItem}
                        user={user}
                        useReplacementButtons={false}
                        valueSaved={item.switchAndSaveItem.amtDiff}
                    />
                )}
                {item.isdiscontinue && (
                    <AlternateProducts
                        alternateProducts={alternateProducts}
                        item={item}
                        user={user}
                    />
                )}
                {item.requiredItems && (
                    <RequiredItems
                        disableRecaptcha={disableRecaptcha}
                        onError={(newErrorMessage: string) => {
                            setErrorMessage(newErrorMessage);
                        }}
                        requiredItems={item.requiredItems}
                        user={user}
                    />
                )}
                {item.assortmentContains && (
                    <AssortmentContains
                        assortmentItems={assortmentItems}
                        user={user}
                    />
                )}
                {item.techSpecs?.length > 0 && <TechSpecs item={item} />}
                {item.description && (
                    <div className="tw-mb-8">
                        <PageHeader
                            title="Product Description"
                            type="h4"
                        />
                        <div dangerouslySetInnerHTML={{__html: item.description}} />
                    </div>
                )}
                {item.frequentlyBoughtTogether && (
                    <FrequentlyBoughtTogether
                        itemTotal={item.frequentlyBoughtTogether.itemTotal}
                        orderableItems={item.frequentlyBoughtTogether.items}
                        user={user}
                    />
                )}
                <div
                    className="tw-mb-8 print:tw-hidden"
                    id="expertlyCraftedJumpto"
                >
                    <ExpertlyCrafted
                        item={item}
                        user={user}
                    />
                </div>
                {item.inAssortmentsItems?.length > 0 && (
                    <AvailableAssortments
                        item={item}
                        user={user}
                    />
                )}
                {item.pageIncludes?.IB && <div dangerouslySetInnerHTML={{__html: item.pageIncludes.IB.html}} />}
                {isInternalUser && item.trainingLinks?.length > 0 && (
                    <div className="tw-mb-8">
                        <PageHeader
                            title="Internal Media Links"
                            type="h4"
                        />
                        {item.trainingLinks.map((mediaItem, index) => {
                            if (mediaItem.type.toLowerCase() === `trainurl`) {
                                return (
                                    <div key={index}>
                                        <a
                                            href={mediaItem.filename}
                                            target="_blank"
                                        >
                                            {mediaItem.comment}
                                        </a>
                                    </div>
                                );
                            }
                            if (mediaItem.type.toLowerCase() === `trainvid`) {
                                return (
                                    <div key={index}>
                                        <a
                                            href={`https://rev.videograinger.com/#/videos/${mediaItem.filename}`}
                                            target="_blank"
                                        >
                                            {mediaItem.comment}
                                        </a>
                                        &nbsp; (The Grainger video site will require a login, use your RACFID and GPASS password to sign in)
                                    </div>
                                );
                            }
                        })}
                    </div>
                )}
                {(caSupportingDocs?.length > 0 || showExceptions) && (
                    <div
                        className="tw-mb-8"
                        data-e2e="compliance&RestrictionsInfo"
                    >
                        <PageHeader
                            title="Compliance & Restrictions"
                            type="h4"
                        />
                        {showExceptions && (
                            <IDPExceptions
                                exceptions={item.exceptions}
                                gdspSdsAsset={item.gdspSdsAsset}
                                user={user}
                            />
                        )}
                        {caSupportingDocs &&
                            caSupportingDocs.map((supportingDocument, index) => (
                                <p key={index}>
                                    <a
                                        href={`/${supportingDocument.fileLocation}`}
                                        target="_blank"
                                    >
                                        <FontAwesomeIcon
                                            icon={faFilePdf}
                                            size="2x"
                                            suppressHydrationWarning
                                        />{' '}
                                        {supportingDocument.name}
                                    </a>
                                </p>
                            ))}
                    </div>
                )}
                {hasDocuments && <SupportingDocuments supportingDocuments={item.supportingDocuments} />}
                {product1_rr && (
                    <div
                        className="tw-mb-8 print:tw-hidden"
                        id="product1_rr_react"
                    >
                        <ItemSpinner
                            componentName={componentName}
                            gaItemListName={`scheme_${product1_rr.scheme}`}
                            label={product1_rr.explanation}
                            loaded
                            maxSlides={7}
                            orderableItems={product1_rr.items}
                            useAddToOrderModal
                            user={user}
                        />
                    </div>
                )}
                {product2_rr && (
                    <div
                        className="tw-mb-8 print:tw-hidden"
                        id="product2_rr_react"
                    >
                        <ItemSpinner
                            componentName={componentName}
                            gaItemListName={`scheme_${product2_rr.scheme}`}
                            label={product2_rr.explanation}
                            loaded
                            maxSlides={7}
                            orderableItems={product2_rr.items}
                            useAddToOrderModal
                            user={user}
                        />
                    </div>
                )}
                <div className="tw-mb-8">
                    <PageHeader
                        title="Item Barcode"
                        type="h4"
                        wrap={true}
                    >
                        <span className="tw-pt-2 tw-mb-0 tw-font-normal -tw-mt-1 print:tw-hidden">
                            {isInternalUser && (
                                <a
                                    className="tw-text-base tw-mr-2"
                                    href={`fsb://item?${item.id}`}
                                    id="productBarcode"
                                    rel="nofollow"
                                >
                                    FSB Barcode Print
                                </a>
                            )}
                            {user.canOrder() && (
                                <Button
                                    onClick={() => {
                                        showBarcodePopUp(item.id, item.yourLabel);
                                    }}
                                    size="sm"
                                    variant="link"
                                >
                                    Generate Barcode
                                </Button>
                            )}
                        </span>
                    </PageHeader>
                    <div className="tw-flex">
                        <div className="tw-text-center">
                            <img
                                alt={item.id}
                                className="tw-h-[56px]"
                                src={`/barcode-generator?code=${item.id}`}
                                style={{imageRendering: `pixelated`}}
                            />
                            <div className="tw-text-center">{item.name}</div>
                        </div>
                    </div>
                </div>
                <IDPTabs
                    isInternalUser={isInternalUser}
                    item={item}
                    user={user}
                />
                {item.pageIncludes?.NT && <div dangerouslySetInnerHTML={{__html: item.pageIncludes.NT.html}} />}
                <p className="tw-text-center print:tw-hidden">
                    For more product information by email, click <a href="/contact-us">here</a>
                </p>
                {isClient && <CompareItems />}
                <CompareModals />
                <AlternateProductsModal
                    alternateProducts={alternateProducts}
                    canEdit={false}
                    onHide={() => {
                        setShowAlternates(false);
                    }}
                    show={showAlternates}
                    showAddToOrder
                    user={user}
                />
                {breadCrumbsSchema && <Helmet script={[helmetJsonLdProp<BreadcrumbList>(breadCrumbsSchema)]} />}
                <Helmet script={[helmetJsonLdProp<Product>(productSchema)]} />
            </div>
        </HelmetProvider>
    );
};
